import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { createAuthHeaders } from "@/lib/auth-utils";
import { getRefreshToken } from "@/lib/token-store";

// --- Types ---

export interface TraceStep {
  emoji?: string;
  message: string;
  status: "running" | "done" | "warn" | "error";
  detail?: string;
  query?: string;
  objective?: string;
  badges?: string[];
  more_count?: number;
  elapsed_ms?: number;
  phase?: string;
}

export interface Message {
  id?: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: string;
  metadata?: any;
  sources?: any[];
  citations?: any[];
  sourceDetails?: any[];
  relatedQuestions?: string[];
  feedback?: "thumbs_up" | "thumbs_down" | null;
}

export interface AgentRun {
  id: string;
  agentType?: string;
  status: "loading" | "streaming" | "completed" | "error" | "rejected";
  messages: Message[];
  traceSteps: TraceStep[];
  metadata?: any;
  pendingInput?: {
    type: "text" | "date" | "select";
    label: string;
    options?: string[];
    field: string;
  } | null;
}

interface AgentExecutionContextType {
  runs: Record<string, AgentRun>;
  activeRunId: string | null;
  startResearchRun: (runId: string, payload: any, apiClient: any, userId: string | null, instructions?: string) => Promise<void>;
  startPredictionRun: (runId: string, payload: any, apiClient: any, userId: string | null, instructions?: string) => Promise<void>;
  startReviewRun: (runId: string, payload: any, apiClient: any, userId: string | null, instructions?: string) => Promise<void>;
  startDespachoReviewRun: (runId: string, payload: any, apiClient: any, userId: string | null, instructions?: string) => Promise<void>;
  startDraftingRun: (runId: string, payload: any, apiClient: any, userId: string | null, instructions?: string) => Promise<void>;
  startComplianceRun: (runId: string, payload: any, apiClient: any, userId: string | null, instructions?: string) => Promise<void>;
  startPatentRun: (runId: string, payload: any, apiClient: any, userId: string | null, instructions?: string) => Promise<void>;
  startWhistleblowerRun: (runId: string, payload: any, apiClient: any, userId: string | null, instructions?: string) => Promise<void>;
  startSpecializedAgentRun: (agentId: string, runId: string, payload: any, apiClient: any, userId: string | null, instructions?: string) => Promise<void>;
  startChatRun: (runId: string, history: Message[], message: string, instructions: string, fileKey: string | null, userId: string | null, apiClient: any, procesoId?: string | null, webSearch?: boolean, focusedDocument?: string | null, skillId?: string | null) => Promise<void>;
  cancelRun: (runId: string) => void;
  resetRun: (runId: string) => void;
  updateRun: (runId: string, updates: Partial<AgentRun>) => void;
  getRun: (runId: string) => AgentRun | undefined;
}

// --- Context ---

const handleStreamResponseError = async (response: Response) => {
  let errorDetail = "";
  try {
    const errData = await response.json();
    if (errData && errData.detail) {
      errorDetail = typeof errData.detail === "string" ? errData.detail : JSON.stringify(errData.detail);
    }
  } catch (e) {}

  if (response.status === 402) {
    const message = errorDetail || "No tienes suficientes créditos para ejecutar esta acción. Por favor recarga tu saldo.";
    toast.error("Créditos Insuficientes", {
      description: message,
      duration: 7000,
    });
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("credits-updated"));
    }
    const err: any = new Error(message);
    err.isCreditError = true;
    throw err;
  }

  throw new Error(errorDetail || `HTTP error! status: ${response.status}`);
};

const AgentExecutionContext = createContext<AgentExecutionContextType | undefined>(undefined);

export const useAgentExecution = () => {
  const context = useContext(AgentExecutionContext);
  if (!context) {
    throw new Error("useAgentExecution must be used within an AgentExecutionProvider");
  }
  return context;
};

export const AgentExecutionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [runs, setRuns] = useState<Record<string, AgentRun>>({});
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const controllers = useRef<Record<string, AbortController>>({});

  const getRun = useCallback((id: string) => runs[id], [runs]);

  const updateRun = useCallback((id: string, updates: Partial<AgentRun>) => {
    setRuns((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || { id, status: "loading", messages: [], traceSteps: [] }),
        ...updates,
      },
    }));
  }, []);

  const cancelRun = useCallback((id: string) => {
    if (controllers.current[id]) {
      controllers.current[id].abort();
      delete controllers.current[id];
    }
    updateRun(id, { status: "completed" }); // Or a dedicated "cancelled" status
    if (activeRunId === id) setActiveRunId(null);
  }, [activeRunId, updateRun]);

  const resetRun = useCallback((id: string) => {
    setRuns((prev) => {
      const newRuns = { ...prev };
      delete newRuns[id];
      return newRuns;
    });
    if (activeRunId === id) setActiveRunId(null);
  }, [activeRunId]);

  // --- Research Agent (Streaming) ---
  const startResearchRun = useCallback(async (runId: string, payload: any, apiClient: any, userId: string | null, instructions?: string) => {
    setActiveRunId(runId);
    updateRun(runId, {
      agentType: "legal-research",
      status: "loading",
      messages: [],
      traceSteps: [],
      metadata: null,
    });

    try {
      // Create request payload, ensuring parent_id is passed
      // Mapping 'query' to 'research_topic' to match LegalResearchRequest schema
      const requestPayload = {
        ...payload,
        research_topic: payload.query,
        user_id: userId,
        parent_id: payload.parent_id || null,
        instructions: instructions
      };
      // Remove 'query' from the final payload to avoid schema validation errors
      delete (requestPayload as any).query;

      // We use the apiClient's baseUrl and auth headers
      const baseUrl = apiClient.baseUrl;
      const authHeaders = await createAuthHeaders();
      const rt = getRefreshToken();
      const headers = {
        ...authHeaders,
        ...(rt ? { "X-Refresh-Token": rt } : {}),
      };

      const abortController = new AbortController();
      controllers.current[runId] = abortController;

      const response = await fetch(`${baseUrl}/api/v1/dashboard/legal-research/investigate/stream`, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestPayload),
        signal: abortController.signal,
      });

      if (!response.ok) await handleStreamResponseError(response);
      if (!response.body) throw new Error("No response body");

      updateRun(runId, { status: "streaming" });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = "";
      let currentTrace: TraceStep[] = [];
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || !trimmedLine.startsWith("data: ")) continue;
          const dataStr = trimmedLine.slice(6).trim();
          if (dataStr === "[DONE]") break;

          try {
            const data = JSON.parse(dataStr);

            if (data.type === "trace") {
              currentTrace = currentTrace.map(s => ({ ...s, status: "done" }));
              currentTrace = [...currentTrace, {
                emoji: data.emoji || "🔎",
                message: data.message || "",
                status: data.status || "running",
                detail: data.detail,
                query: data.query,
                objective: data.objective,
                badges: data.badges,
                more_count: data.more_count,
                elapsed_ms: data.elapsed_ms,
                phase: data.phase || (data.metadata && data.metadata.phase),
              }];

              updateRun(runId, { traceSteps: currentTrace });
            } else if (data.type === "chunk") {
              const content = data.content || "";
              setRuns((prev) => {
                const run = prev[runId];
                if (!run) return prev;
                const metadata = run.metadata || {};

                let newAnswer = (metadata.answer || "") + content;
                newAnswer = newAnswer.replace(/```(?:json|markdown|txt)?\s*/gi, "");
                newAnswer = newAnswer.replace(/```\s*$/g, "");

                return {
                  ...prev,
                  [runId]: {
                    ...run,
                    metadata: {
                      ...metadata,
                      answer: newAnswer
                    }
                  }
                };
              });
            } else if (data.type === "meta" || data.type === "result") {
              const metadata = data.type === "result" ? data.data : data;
              updateRun(runId, { metadata });
            } else if (data.type === "extracted_text") {
              setRuns((prev) => {
                const run = prev[runId];
                if (!run) return prev;
                return {
                  ...prev,
                  [runId]: {
                    ...run,
                    metadata: { ...(run.metadata || {}), extracted_text: data.content }
                  }
                };
              });
            } else if (data.type === "error") {
              toast.error(data.message || "Error en el agente");
              updateRun(runId, { status: "error" });
            }
          } catch (e) {
            console.error("Error parsing SSE data:", e);
          }
        }
      }

      updateRun(runId, {
        status: "completed",
        traceSteps: currentTrace.map(s => ({ ...s, status: "done" }))
      });
      playSuccessSound();
      toast.success("Revisión finalizada");
      window.dispatchEvent(new CustomEvent("credits-updated"));
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error("Review Run Error:", error);
      updateRun(runId, { status: "error" });
      if (!error?.isCreditError && !error?.message?.includes("Créditos insuficientes") && !error?.message?.includes("Créditos Insuficientes")) {
        toast.error(`Error de revisión: ${error.message}`);
      }
    } finally {
      delete controllers.current[runId];
      setActiveRunId(null);
    }
  }, [updateRun]);

  // --- Case Prediction Agent (Streaming) ---
  const startPredictionRun = useCallback(async (runId: string, payload: any, apiClient: any, userId: string | null, instructions?: string) => {
    setActiveRunId(runId);
    updateRun(runId, {
      agentType: "case-prediction",
      status: "loading",
      messages: [],
      traceSteps: [],
      metadata: null,
    });

    const abortController = new AbortController();
    controllers.current[runId] = abortController;

    try {
      const baseUrl = apiClient.baseUrl;
      const authHeaders = await createAuthHeaders();
      const rt = getRefreshToken();
      const headers = {
        ...authHeaders,
        ...(rt ? { "X-Refresh-Token": rt } : {}),
      };

      const response = await fetch(`${baseUrl}/api/v1/dashboard/case-prediction/analyze/stream`, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...payload, instructions }),
        signal: abortController.signal,
      });

      if (!response.ok) await handleStreamResponseError(response);
      if (!response.body) throw new Error("No response body");

      updateRun(runId, { status: "streaming" });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let currentTrace: TraceStep[] = [];
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || !trimmedLine.startsWith("data: ")) continue;
          const dataStr = trimmedLine.slice(6).trim();
          if (dataStr === "[DONE]") break;

          try {
            const data = JSON.parse(dataStr);
            if (data.type === "trace") {
              currentTrace = currentTrace.map(s => ({ ...s, status: "done" }));
              const newStep: TraceStep = {
                emoji: data.emoji || "🔹",
                message: data.message || "",
                status: data.status || "running",
                detail: data.detail,
                query: data.query,
                objective: data.objective,
                badges: data.badges,
                more_count: data.more_count,
              };
              currentTrace = [...currentTrace, newStep];
              updateRun(runId, { traceSteps: currentTrace });
            } else if (data.type === "result") {
              updateRun(runId, { metadata: data.data || data });
            } else if (data.type === "error") {
              toast.error(data.message || "Error en el agente de predicción");
              updateRun(runId, { status: "error" });
            }
          } catch (e) {
            console.error("Error parsing SSE data:", e);
          }
        }
      }

      updateRun(runId, { status: "completed" });
      playSuccessSound();
      toast.success("Predicción de caso finalizada");
    } catch (error: any) {
      if (error.name === 'AbortError') return;
      console.error("Prediction Run Error:", error);
      updateRun(runId, { status: "error" });
      toast.error(`Error de predicción: ${error.message}`);
    } finally {
      delete controllers.current[runId];
      setActiveRunId(null);
    }
  }, [updateRun]);

  // --- Despachos Review Agent (Streaming) ---
  const startDespachoReviewRun = useCallback(async (runId: string, payload: any, apiClient: any, userId: string | null, instructions?: string) => {
    setActiveRunId(runId);
    updateRun(runId, {
      agentType: "contract-review",
      status: "loading",
      messages: [],
      traceSteps: [],
      metadata: null,
    });

    const abortController = new AbortController();
    controllers.current[runId] = abortController;

    try {
      const baseUrl = apiClient.baseUrl;
      const authHeaders = await createAuthHeaders();
      const rt = getRefreshToken();
      const headers = {
        ...authHeaders,
        ...(rt ? { "X-Refresh-Token": rt } : {}),
      };

      const response = await fetch(`${baseUrl}/api/v1/contract-review/analyze/stream`, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...payload, instructions }),
        signal: abortController.signal,
      });

      if (!response.ok) await handleStreamResponseError(response);
      if (!response.body) throw new Error("No response body");

      updateRun(runId, { status: "streaming" });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let currentTrace: TraceStep[] = [];
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || !trimmedLine.startsWith("data: ")) continue;
          const dataStr = trimmedLine.slice(6).trim();
          if (dataStr === "[DONE]") break;

          try {
            const data = JSON.parse(dataStr);
            if (data.type === "trace") {
              currentTrace = currentTrace.map(s => ({ ...s, status: "done" }));
              const newStep: TraceStep = {
                emoji: data.emoji || "🔹",
                message: data.message || "",
                status: data.status || "running",
                detail: data.detail,
              };
              currentTrace = [...currentTrace, newStep];
              updateRun(runId, { traceSteps: currentTrace });
            } else if (data.type === "chunk") {
              const content = data.content || "";
              setRuns((prev) => {
                const run = prev[runId];
                if (!run) return prev;
                const metadata = run.metadata || {};
                let newAnswer = (metadata.answer || "") + content;
                newAnswer = newAnswer.replace(/```(?:json|markdown|txt)?\s*/gi, "");
                newAnswer = newAnswer.replace(/```\s*$/g, "");
                return {
                  ...prev,
                  [runId]: {
                    ...run,
                    metadata: { ...metadata, answer: newAnswer }
                  }
                };
              });
            } else if (data.type === "meta" || data.type === "result") {
              const metadata = data.type === "result" ? data.data : data;
              updateRun(runId, { metadata });
            } else if (data.type === "extracted_text") {
              setRuns((prev) => {
                const run = prev[runId];
                if (!run) return prev;
                return {
                  ...prev,
                  [runId]: {
                    ...run,
                    metadata: { ...(run.metadata || {}), extracted_text: data.content }
                  }
                };
              });
            } else if (data.type === "error") {
              toast.error(data.message || "Error en el agente");
              updateRun(runId, { status: "error" });
            }
          } catch (e) {
            console.error("Error parsing SSE data:", e);
          }
        }
      }

      updateRun(runId, {
        status: "completed",
        traceSteps: currentTrace.map(s => ({ ...s, status: "done" }))
      });
      playSuccessSound();
      toast.success("Análisis de expediente finalizado");
      window.dispatchEvent(new CustomEvent("credits-updated"));
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error("Despacho Review Run Error:", error);
      updateRun(runId, { status: "error" });
      if (!error?.isCreditError && !error?.message?.includes("Créditos insuficientes") && !error?.message?.includes("Créditos Insuficientes")) {
        toast.error(`Error de análisis: ${error.message}`);
      }
    } finally {
      delete controllers.current[runId];
      setActiveRunId(null);
    }
  }, [updateRun]);

  // Alias for backward compatibility
  const startReviewRun = startDespachoReviewRun;

  // --- Drafting Agent (Streaming) ---
  const startDraftingRun = useCallback(async (runId: string, payload: any, apiClient: any, userId: string | null, instructions?: string) => {
    setActiveRunId(runId);
    updateRun(runId, {
      agentType: "document-drafting",
      status: "loading",
      messages: [],
      traceSteps: [],
      metadata: null,
    });

    try {
      const requestPayload = {
        document_type: payload.documentType,
        description: payload.description,
        parties: payload.parties,
        key_points: payload.keyPoints,
        industry: payload.industry,
        jurisdiction: payload.jurisdiction,
        complexity: payload.complexity,
        hf_metadata: payload.hf_metadata,
        instructions: instructions,
        template_path: payload.template_path,
        case_id: payload.case_id,
        complementary_file_keys: payload.complementary_file_keys
      };

      const baseUrl = apiClient.baseUrl;
      const authHeaders = await createAuthHeaders();
      const rt = getRefreshToken();
      const headers = {
        ...authHeaders,
        ...(rt ? { "X-Refresh-Token": rt } : {}),
      };

      const abortController = new AbortController();
      controllers.current[runId] = abortController;

      const response = await fetch(`${baseUrl}/api/v1/draft-custom-document/stream`, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestPayload),
        signal: abortController.signal,
      });

      if (!response.ok) await handleStreamResponseError(response);
      if (!response.body) throw new Error("No response body");

      updateRun(runId, { status: "streaming" });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let currentTrace: TraceStep[] = [];
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || !trimmedLine.startsWith("data: ")) continue;
          const dataStr = trimmedLine.slice(6).trim();
          if (dataStr === "[DONE]") break;

          try {
            const data = JSON.parse(dataStr);
            if (data.type === "trace") {
              currentTrace = currentTrace.map(s => ({ ...s, status: "done" }));
              currentTrace = [...currentTrace, {
                emoji: data.emoji || "🔹",
                message: data.message || "",
                status: data.status || "running",
                detail: data.detail,
              }];
              updateRun(runId, { traceSteps: currentTrace });
            } else if (data.type === "chunk") {
              let content = data.content || "";

              setRuns((prev) => {
                const run = prev[runId];
                if (!run) return prev;
                const metadata = run.metadata || {};
                const document = metadata.document || {};

                let newContent = (document.content || "") + content;

                newContent = newContent.replace(/```(?:markdown|txt|text|html)?\s*/gi, "");

                newContent = newContent.replace(/```\s*$/g, "");

                newContent = newContent.replace(/\\n/g, "\n").replace(/\\"/g, '"').replace(/\\'/g, "'");

                return {
                  ...prev,
                  [runId]: {
                    ...run,
                    metadata: {
                      ...metadata,
                      document: {
                        ...document,
                        content: newContent
                      }
                    }
                  }
                };
              });
            } else if (data.type === "meta" || data.type === "result") {
              const metadata = data.type === "result" ? data.data : data;
              updateRun(runId, { metadata });
            } else if (data.type === "error") {
              toast.error(data.message || "Error en el agente");
              updateRun(runId, { status: "error" });
            }
          } catch (e) {
            console.error("Error parsing SSE data:", e);
          }
        }
      }

      updateRun(runId, { status: "completed" });
      playSuccessSound();
      toast.success("Documento redactado con éxito");
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error("Drafting Run Error:", error);
      updateRun(runId, { status: "error" });
      if (!error?.isCreditError && !error?.message?.includes("Créditos insuficientes") && !error?.message?.includes("Créditos Insuficientes")) {
        toast.error(`Error de redacción: ${error.message}`);
      }
    } finally {
      setActiveRunId(null);
    }
  }, [updateRun]);

  // --- Compliance Agent (Streaming) ---
  const startComplianceRun = useCallback(async (runId: string, payload: any, apiClient: any, userId: string | null) => {
    setActiveRunId(runId);
    updateRun(runId, {
      agentType: "compliance-monitoring",
      status: "loading",
      messages: [],
      traceSteps: [],
      metadata: null,
    });

    try {
      const baseUrl = apiClient.baseUrl;
      const authHeaders = await createAuthHeaders();
      const rt = getRefreshToken();
      const headers = {
        ...authHeaders,
        ...(rt ? { "X-Refresh-Token": rt } : {}),
      };

      const response = await fetch(`${baseUrl}/api/v1/regulatory/analyze/stream`, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) await handleStreamResponseError(response);
      if (!response.body) throw new Error("No response body");

      updateRun(runId, { status: "streaming" });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let currentTrace: TraceStep[] = [];
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || !trimmedLine.startsWith("data: ")) continue;
          const dataStr = trimmedLine.slice(6).trim();
          if (dataStr === "[DONE]") break;

          try {
            const data = JSON.parse(dataStr);
            if (data.type === "trace") {
              currentTrace = currentTrace.map(s => ({ ...s, status: "done" }));
              currentTrace = [...currentTrace, {
                emoji: data.emoji || "🔹",
                message: data.message || "",
                status: data.status || "running",
                detail: data.detail,
              }];
              updateRun(runId, { traceSteps: currentTrace });
            } else if (data.type === "result") {
              updateRun(runId, { metadata: data.data || data });
            } else if (data.type === "error") {
              toast.error(data.message || "Error en el agente");
              updateRun(runId, { status: "error" });
            }
          } catch (e) {
            console.error("Error parsing SSE data:", e);
          }
        }
      }

      updateRun(runId, {
        status: "completed",
        traceSteps: currentTrace.map(s => ({ ...s, status: "done" }))
      });
      playSuccessSound();
      toast.success("Análisis de cumplimiento completado");
    } catch (error: any) {
      console.error("Compliance Run Error:", error);
      updateRun(runId, { status: "error" });
      if (!error?.isCreditError && !error?.message?.includes("Créditos insuficientes") && !error?.message?.includes("Créditos Insuficientes")) {
        toast.error(`Error de cumplimiento: ${error.message}`);
      }
    } finally {
      setActiveRunId(null);
    }
  }, [updateRun]);

  // --- Patent Agent (Streaming) ---
  const startPatentRun = useCallback(async (runId: string, payload: any, apiClient: any, userId: string | null) => {
    setActiveRunId(runId);
    updateRun(runId, {
      agentType: "patent-search",
      status: "loading",
      messages: [],
      traceSteps: [],
      metadata: null,
    });

    try {
      const baseUrl = apiClient.baseUrl;
      const authHeaders = await createAuthHeaders();
      const rt = getRefreshToken();
      const headers = {
        ...authHeaders,
        ...(rt ? { "X-Refresh-Token": rt } : {}),
      };

      const response = await fetch(`${baseUrl}/api/v1/patent/analyze/stream`, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          invention_description: payload.solution_description,
          jurisdiction: payload.technical_field,
          prior_art: [],
          technical_field: payload.technical_field,
          legal_terms: []
        }),
      });

      if (!response.ok) await handleStreamResponseError(response);
      if (!response.body) throw new Error("No response body");

      updateRun(runId, { status: "streaming" });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let currentTrace: TraceStep[] = [];
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || !trimmedLine.startsWith("data: ")) continue;
          const dataStr = trimmedLine.slice(6).trim();
          if (dataStr === "[DONE]") break;

          try {
            const data = JSON.parse(dataStr);
            if (data.type === "trace") {
              currentTrace = currentTrace.map(s => ({ ...s, status: "done" }));
              currentTrace = [...currentTrace, {
                emoji: data.emoji || "🔹",
                message: data.message || "",
                status: data.status || "running",
                detail: data.detail,
              }];
              updateRun(runId, { traceSteps: currentTrace });
            } else if (data.type === "result") {
              updateRun(runId, { metadata: data.data || data });
            } else if (data.type === "error") {
              toast.error(data.message || "Error en el agente");
              updateRun(runId, { status: "error" });
            }
          } catch (e) {
            console.error("Error parsing SSE data:", e);
          }
        }
      }

      updateRun(runId, {
        status: "completed",
        traceSteps: currentTrace.map(s => ({ ...s, status: "done" }))
      });
      playSuccessSound();
      toast.success("Búsqueda de patente finalizada");
    } catch (error: any) {
      console.error("Patent Run Error:", error);
      updateRun(runId, { status: "error" });
      if (!error?.isCreditError && !error?.message?.includes("Créditos insuficientes") && !error?.message?.includes("Créditos Insuficientes")) {
        toast.error(`Error de patentes: ${error.message}`);
      }
    } finally {
      setActiveRunId(null);
    }
  }, [updateRun]);

  // --- Whistleblower Agent (Streaming) ---
  const startWhistleblowerRun = useCallback(async (runId: string, payload: any, apiClient: any, userId: string | null) => {
    setActiveRunId(runId);
    updateRun(runId, {
      agentType: "whistleblower",
      status: "loading",
      messages: [],
      traceSteps: [],
      metadata: null,
    });

    try {
      const baseUrl = apiClient.baseUrl;
      const authHeaders = await createAuthHeaders();
      const rt = getRefreshToken();
      const headers = {
        ...authHeaders,
        ...(rt ? { "X-Refresh-Token": rt } : {}),
      };

      const response = await fetch(`${baseUrl}/api/v1/whistleblower/report/analyze/stream`, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          report_content: payload.incident_description,
          report_type: payload.report_type,
          jurisdiction: "Colombia",
          specific_concerns: [],
          data_processing: {
            type: payload.is_anonymous ? "anonymous" : "regular",
            consent: { provided: true }
          },
          legal_terms: []
        }),
      });

      if (!response.ok) await handleStreamResponseError(response);
      if (!response.body) throw new Error("No response body");

      updateRun(runId, { status: "streaming" });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let currentTrace: TraceStep[] = [];
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || !trimmedLine.startsWith("data: ")) continue;
          const dataStr = trimmedLine.slice(6).trim();
          if (dataStr === "[DONE]") break;

          try {
            const data = JSON.parse(dataStr);
            if (data.type === "trace") {
              currentTrace = currentTrace.map(s => ({ ...s, status: "done" }));
              currentTrace = [...currentTrace, {
                emoji: data.emoji || "🔹",
                message: data.message || "",
                status: data.status || "running",
                detail: data.detail,
              }];
              updateRun(runId, { traceSteps: currentTrace });
            } else if (data.type === "result") {
              updateRun(runId, { metadata: data.data || data });
            } else if (data.type === "error") {
              toast.error(data.message || "Error en el agente");
              updateRun(runId, { status: "error" });
            }
          } catch (e) {
            console.error("Error parsing SSE data:", e);
          }
        }
      }

      updateRun(runId, {
        status: "completed",
        traceSteps: currentTrace.map(s => ({ ...s, status: "done" }))
      });
      playSuccessSound();
      toast.success("Gestión de denuncia finalizada");
    } catch (error: any) {
      console.error("Whistleblower Run Error:", error);
      updateRun(runId, { status: "error" });
      if (!error?.isCreditError && !error?.message?.includes("Créditos insuficientes") && !error?.message?.includes("Créditos Insuficientes")) {
        toast.error(`Error de denuncias: ${error.message}`);
      }
    } finally {
      setActiveRunId(null);
    }
  }, [updateRun]);

  // --- Specialized Agent (Modular Streaming) ---
  const startSpecializedAgentRun = useCallback(async (agentId: string, runId: string, payload: any, apiClient: any, userId: string | null) => {
    setActiveRunId(runId);

    const existingRun = getRun(runId);
    const existingHistory = payload.history || existingRun?.messages || [];

    const newUserMessage: Message = {
      role: "user",
      content: payload.message,
      timestamp: new Date().toISOString()
    };

    const lastMsg = existingHistory[existingHistory.length - 1];
    const finalHistory = (lastMsg?.role === "user" && lastMsg?.content === payload.message)
      ? existingHistory
      : [...existingHistory, newUserMessage];

    updateRun(runId, {
      agentType: `specialized-${agentId}`,
      status: "loading",
      messages: finalHistory,
      traceSteps: existingRun?.traceSteps || [],
      metadata: existingRun?.metadata || null,
    });

    try {
      const baseUrl = apiClient.baseUrl;
      const authHeaders = await createAuthHeaders();
      const rt = getRefreshToken();
      const headers = {
        ...authHeaders,
        ...(rt ? { "X-Refresh-Token": rt } : {}),
      };
      const abortController = new AbortController();
      controllers.current[runId] = abortController;

      const response = await fetch(`${baseUrl}/api/v1/agents/${agentId}/ask`, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: payload.message,
          session_id: payload.session_id || runId,
          history: finalHistory.slice(0, -1)
        }),
        signal: abortController.signal,
      });

      if (!response.ok) await handleStreamResponseError(response);
      if (!response.body) throw new Error("No response body");

      updateRun(runId, { status: "streaming" });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = "";
      let currentTrace: TraceStep[] = [];
      let currentMetadata: any = {};
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine) continue;

          let dataStr = trimmedLine;
          if (trimmedLine.startsWith("data: ")) {
            dataStr = trimmedLine.slice(6).trim();
          }

          if (dataStr === "[DONE]") {
            updateRun(runId, { status: "completed" });
            break;
          }

          try {
            if (dataStr.startsWith("{") && dataStr.includes("\"type\"")) {
              const data = JSON.parse(dataStr);

              if (data.type === "trace") {
                currentTrace = currentTrace.map(s => ({ ...s, status: "done" }));
                currentTrace = [...currentTrace, {
                  emoji: data.emoji || "🔹",
                  message: data.message || "",
                  status: data.status || "running",
                  detail: data.detail,
                }];
                updateRun(runId, { traceSteps: [...currentTrace] });
              } else if (data.type === "chunk") {
                fullContent += (data.content || "");
                currentMetadata = { ...currentMetadata, answer: fullContent };
                updateRun(runId, { metadata: { ...currentMetadata } });
              } else if (data.type === "citations") {
                currentMetadata = { ...currentMetadata, citations: data.data };
                updateRun(runId, { metadata: { ...currentMetadata } });
              } else if (data.type === "insights") {
                currentMetadata = { ...currentMetadata, insights: data.data };
                updateRun(runId, { metadata: { ...currentMetadata } });
              } else if (data.type === "suggestions") {
                currentMetadata = { ...currentMetadata, suggestions: data.data };
                updateRun(runId, { metadata: { ...currentMetadata } });
              } else if (data.type === "document_update") {
                currentMetadata = { ...currentMetadata, document_update: data.content, last_sync: new Date().toISOString() };
                updateRun(runId, { metadata: { ...currentMetadata } });
              } else if (data.type === "interactive_input") {
                updateRun(runId, { pendingInput: data.data });
                updateRun(runId, { status: "completed" });
              } else if (data.type === "error") {
                updateRun(runId, { status: "error" });
                toast.error(typeof data.message === 'object' ? JSON.stringify(data.message) : String(data.message || "Error en el agente"));
              }
            } else {
              fullContent += dataStr;
              currentMetadata = { ...currentMetadata, answer: fullContent };
              updateRun(runId, { metadata: { ...currentMetadata } });
            }
          } catch (e) {
            console.error("Parse error in specialized agent stream:", e, dataStr);
            if (!dataStr.startsWith("{")) {
              fullContent += dataStr;
              currentMetadata = { ...currentMetadata, answer: fullContent };
              updateRun(runId, { metadata: { ...currentMetadata } });
            }
          }
        }
      }

      updateRun(runId, {
        status: "completed",
        metadata: { ...currentMetadata, answer: fullContent }
      });
      playSuccessSound();

    } catch (error: any) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error("Specialized Agent Run Error:", error);
      updateRun(runId, { status: "error" });
      if (!error?.isCreditError && !error?.message?.includes("Créditos insuficientes") && !error?.message?.includes("Créditos Insuficientes")) {
        toast.error(`Error en agente especializado: ${typeof error.message === 'object' ? JSON.stringify(error.message) : String(error.message || "")}`);
      }
    } finally {
      delete controllers.current[runId];
      setActiveRunId(null);
    }
  }, [updateRun, getRun]);

  // --- Chat Agent (Streaming) ---
  const startChatRun = useCallback(async (
    runId: string,
    history: Message[],
    message: string,
    instructions: string,
    fileKey: string | null,
    userId: string | null,
    apiClient: any,
    procesoId: string | null = null,
    webSearch: boolean = false,
    focusedDocument: string | null = null,
    skillId: string | null = null,
    interactionMode: string = "general"
  ) => {
    setActiveRunId(runId);
    const startTime = Date.now();

    // Create the message with instructions if needed
    const finalUserContent = instructions
      ? `${message}\n\n[Instrucciones para la IA: ${instructions}]`
      : message;

    const initialMessage: Message = {
      role: "user",
      content: finalUserContent,
      timestamp: new Date().toISOString()
    };

    updateRun(runId, {
      agentType: "legal-chatbot",
      status: "loading",
      messages: [...history, initialMessage],
      traceSteps: [{ emoji: "🔍", message: "Iniciando análisis y consulta jurídica...", status: "running" }],
    });

    try {
      const baseUrl = apiClient.baseUrl;
      const authHeaders = await createAuthHeaders();
      const rt = getRefreshToken();
      const headers = {
        ...authHeaders,
        ...(rt ? { "X-Refresh-Token": rt } : {}),
      };

      const response = await fetch(`${baseUrl}/api/v1/ask/stream`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          message: finalUserContent,
          user_id: userId,
          file_key: fileKey,
          conversation_id: runId,
          history: history,
          proceso_id: procesoId,
          web_search: webSearch,
          focused_document: focusedDocument,
          skill_id: skillId,
          interaction_mode: interactionMode
        }),
      });

      if (!response.ok) await handleStreamResponseError(response);

      updateRun(runId, { status: "streaming" });

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let assistantContent = "";
      let currentTrace: TraceStep[] = [];
      let buffer = "";

      while (reader) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (!trimmedLine || !trimmedLine.startsWith("data: ")) continue;
          const dataStr = trimmedLine.slice(6).trim();
          if (dataStr === "[DONE]") break;

          try {
            const data = JSON.parse(dataStr);
            if (data.type === "chunk") {
              assistantContent += data.content;

              if (currentTrace.length === 0) {
                currentTrace = [
                  { emoji: "🔍", message: "Búsqueda jurídica y recuperación de fuentes", status: "done" },
                  { emoji: "📑", message: "Análisis jurisprudencial y normativo", status: "done" },
                  { emoji: "✍️", message: "Generando respuesta jurídica fundamentada...", status: "running" }
                ];
                updateRun(runId, { traceSteps: currentTrace });
              } else {
                const lastStep = currentTrace[currentTrace.length - 1];
                if (lastStep.message !== "Generando respuesta...") {
                  currentTrace = currentTrace.map(s => ({ ...s, status: "done" }));
                  currentTrace.push({
                    emoji: "✍️",
                    message: "Generando respuesta...",
                    status: "running"
                  });
                  updateRun(runId, { traceSteps: currentTrace });
                }
              }

              // --- Real-time Question & Action Parsing ---
              let displayContent = assistantContent;
              let relatedQuestions: string[] = [];
              let suggestedActions: any[] = [];

              const qMarker = assistantContent.toLowerCase().indexOf("---questions---");
              const aMarker = assistantContent.toLowerCase().indexOf("---actions---");

              // Clean text is anything before the FIRST marker found
              let firstMarker = -1;
              if (qMarker !== -1 && aMarker !== -1) firstMarker = Math.min(qMarker, aMarker);
              else if (qMarker !== -1) firstMarker = qMarker;
              else if (aMarker !== -1) firstMarker = aMarker;

              if (firstMarker !== -1) {
                displayContent = assistantContent.substring(0, firstMarker).trim();

                // Extract questions
                if (qMarker !== -1) {
                  const qEnd = (aMarker > qMarker) ? aMarker : assistantContent.length;
                  const qPart = assistantContent.substring(qMarker + 15, qEnd).trim();
                  relatedQuestions = qPart.split(/\||\n|•|\*/).map(q => q.trim()).filter(q => q.length > 5 && q.includes('?')).slice(0, 3);
                }

                // Extract actions
                if (aMarker !== -1) {
                  const aEnd = (qMarker > aMarker) ? qMarker : assistantContent.length;
                  const aPart = assistantContent.substring(aMarker + 13, aEnd).trim();
                  const actionsRaw = aPart.split(/\||\n|•|\*/).map(a => a.trim()).filter(a => a.length > 3);
                  suggestedActions = actionsRaw.map(a => ({ label: a, prompt: a }));
                }
              }

              const assistantMessage: Message = {
                role: "assistant",
                content: displayContent,
                timestamp: new Date().toISOString(),
                relatedQuestions: relatedQuestions.length > 0 ? relatedQuestions : undefined,
                metadata: {
                  ...(suggestedActions.length > 0 ? { recommendations: suggestedActions } : {}),
                  ...(currentTrace.length > 0 ? { traceSteps: [...currentTrace] } : {})
                }
              };
              updateRun(runId, { messages: [...history, initialMessage, assistantMessage] });
            } else if (data.type === "trace") {
              currentTrace = currentTrace.map(s => ({ ...s, status: "done" }));
              currentTrace = [...currentTrace, {
                emoji: data.emoji || "🔹",
                message: data.message || "",
                status: data.status || "running",
                detail: data.detail,
                query: data.query,
                objective: data.objective,
                badges: data.badges,
                more_count: data.more_count,
                elapsed_ms: data.elapsed_ms,
                phase: data.phase || data.metadata?.phase,
              }];
              updateRun(runId, { traceSteps: currentTrace });
            } else if (data.type === "citations" || data.type === "sources") {
              const items = data.data || data.items || [];
              setRuns((prev) => {
                const run = prev[runId];
                if (run && run.messages.length > 0) {
                  const lastIdx = run.messages.length - 1;
                  if (run.messages[lastIdx].role === "assistant") {
                    const updatedMessages = [...run.messages];
                    updatedMessages[lastIdx] = {
                      ...updatedMessages[lastIdx],
                      sourceDetails: items,
                      metadata: {
                        ...updatedMessages[lastIdx].metadata,
                        source_details: items
                      }
                    };
                    return { ...prev, [runId]: { ...run, messages: updatedMessages } };
                  }
                }
                return prev;
              });
            } else if (data.type === "meta" || data.type === "result") {
              const metaData = data.type === "result" ? data.data : data;
              updateRun(runId, { metadata: metaData });

              // Enrich the last assistant message with these recommendations/metadata/sources
              setRuns((prev) => {
                const run = prev[runId];
                if (run && run.messages.length > 0) {
                  const lastIdx = run.messages.length - 1;
                  if (run.messages[lastIdx].role === "assistant") {
                    const updatedMessages = [...run.messages];
                    updatedMessages[lastIdx] = {
                      ...updatedMessages[lastIdx],
                      sourceDetails: (Array.isArray(metaData.source_details) && metaData.source_details.length > 0)
                        ? metaData.source_details
                        : (Array.isArray(metaData.sources) && metaData.sources.length > 0)
                        ? metaData.sources
                        : updatedMessages[lastIdx].sourceDetails,
                      relatedQuestions: (Array.isArray(metaData.contextual_questions) && metaData.contextual_questions.length > 0)
                        ? metaData.contextual_questions
                        : (Array.isArray(metaData.relatedQuestions) && metaData.relatedQuestions.length > 0)
                        ? metaData.relatedQuestions
                        : updatedMessages[lastIdx].relatedQuestions,
                      metadata: {
                        ...updatedMessages[lastIdx].metadata,
                        ...metaData
                      },
                      ...(metaData.recommendations ? { recommendations: metaData.recommendations } : {})
                    };
                    return { ...prev, [runId]: { ...run, messages: updatedMessages } };
                  }
                }
                return prev;
              });
            }
          } catch (e) { }
        }
      }

      const finalCompletedTrace = (currentTrace.length > 0 ? currentTrace : [
        { emoji: "🔍", message: "Búsqueda jurídica y recuperación de fuentes", status: "done" },
        { emoji: "📑", message: "Análisis jurisprudencial y normativo", status: "done" },
        { emoji: "⚖️", message: "Generación de respuesta jurídica fundamentada", status: "done" }
      ]).map(s => ({ ...s, status: "done" as const }));

      const totalDurationSec = parseFloat(((Date.now() - startTime) / 1000).toFixed(1));

      setRuns((prev) => {
        const run = prev[runId];
        if (run && run.messages.length > 0) {
          const lastIdx = run.messages.length - 1;
          if (run.messages[lastIdx].role === "assistant") {
            const updatedMessages = [...run.messages];
            const execSeconds = updatedMessages[lastIdx].metadata?.execution_time_seconds || totalDurationSec;
            updatedMessages[lastIdx] = {
              ...updatedMessages[lastIdx],
              metadata: {
                ...updatedMessages[lastIdx].metadata,
                execution_time_seconds: execSeconds,
                traceSteps: finalCompletedTrace
              }
            };
            try {
              localStorage.setItem(`chat_trace_${runId}_${lastIdx}`, JSON.stringify(finalCompletedTrace));
            } catch (e) {}
            return {
              ...prev,
              [runId]: {
                ...run,
                status: "completed",
                metadata: {
                  ...run.metadata,
                  execution_time_seconds: run.metadata?.execution_time_seconds || execSeconds,
                },
                messages: updatedMessages,
                traceSteps: finalCompletedTrace
              }
            };
          }
        }
        return {
          ...prev,
          [runId]: {
            ...run,
            status: "completed",
            metadata: {
              ...run.metadata,
              execution_time_seconds: run?.metadata?.execution_time_seconds || totalDurationSec,
            },
            traceSteps: finalCompletedTrace
          }
        };
      });
      playSuccessSound();
      toast.success("Agente finalizó su ejecución");
    } catch (error: any) {
      console.error("Chat Run Error:", error);
      updateRun(runId, { status: "error" });
      if (!error?.isCreditError && !error?.message?.includes("Créditos insuficientes") && !error?.message?.includes("Créditos Insuficientes")) {
        toast.error(`Error de chat: ${typeof error.message === 'object' ? JSON.stringify(error.message) : String(error.message || "")}`);
      }
    } finally {
      setActiveRunId(null);
    }
  }, [updateRun]);

  const playSuccessSound = () => {
    try {
      const audio = new Audio("https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3");
      audio.volume = 0.5;
      audio.play().catch(e => console.warn("Audio play blocked:", e));
    } catch (e) {
      console.warn("Audio failed:", e);
    }
  };

  return (
    <AgentExecutionContext.Provider
      value={{
        runs,
        activeRunId,
        startResearchRun,
        startPredictionRun,
        startReviewRun,
        startDespachoReviewRun,
        startDraftingRun,
        startComplianceRun,
        startPatentRun,
        startWhistleblowerRun,
        startSpecializedAgentRun,
        startChatRun,
        cancelRun,
        resetRun,
        updateRun,
        getRun,
      }}
    >
      {children}
    </AgentExecutionContext.Provider>
  );
};
